import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import MCard from "components/Shared/MCard.tsx";
import { MSpinner } from "components/Shared";
import { DATA_TYPE_DROPDOWN2, DATA_TYPE_STRING, DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";
import { getAppraiserAssignmentApproval, setAppraiserAssignmentApproval } from "requests/appraiserAssignmentApproval";
import moment from "moment-timezone";
import { userHasRight } from "selectors/rights";
import { APPRAISAL_ASSIGNMENT_APPROVAL_VIEW, APPRAISAL_ASSIGNMENT_APPROVAL_EDIT } from "typeConstants/rights";
import * as yup from "yup";
import responseHandler from "responseHandler";

const AppraiserAssignmentApproval = ({
  controlNumber,
  isEditable,
  isVisible,
  internetUser,
  makeSuccessToast,
}) => {
  const [assignmentApproval, setAssignmentApproval] = useState(null);
  const [fetchingAssignmentApproval, setFetchingAssignmentApproval] = useState(true);
  const [sendingAssignmentApproval, setSendingAssignmentApproval] = useState(false);
  const [cardControlMethods, setCardControlMethods] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    getAppraiserAssignmentApproval(controlNumber, (resp) => {
      setAssignmentApproval(resp);
      setFetchingAssignmentApproval(false);
    }, () => {
      setFetchingAssignmentApproval(false);
    }, mounted);
    return () => {
      mounted.current = false;
    };
  }, []);

  const transformApprovalStatus = () => {
    if(assignmentApproval) {
      if(assignmentApproval.decisionMade) {
        if(assignmentApproval.decisionApproved) return "Approved";
        else return "Declined";
      }
    }
    return "Pending";
  };

  const transformApprovalDecision = () => {
    if(assignmentApproval) {
      if(assignmentApproval.decisionMade) {
        if(assignmentApproval.decisionApproved) return {label: "Approved", value: "Approved"};
        else return {label: "Declined", value: "Declined"};
      }
    }
    return null;
  };

  const isApproved = (status) => {
    if(status === "Approved") return true;
    else return false;
  };

  const isDecided = () => {
    return true;
  };

  const getFields = () => {
    let fields = [];
    if(!assignmentApproval.decisionMade){
      fields.push({
        key: "approvalStatus",
        label: "Status",
        dataType: DATA_TYPE_STRING,
        value: transformApprovalStatus(),
        editable: false
      });
    }
    fields.push(
      {
        key: "approvalDecision",
        value: transformApprovalDecision(),
        label: "Decision",
        editable: !assignmentApproval.decisionMade,
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: {
          options: [
            {label: "Approved", value: "Approved"},
            {label: "Declined", value: "Declined"}
          ]
        },
        validation: yup.string().nullable().required("Status is a required field")
      },
      {
        key: "appraiserSelected",
        label: "Appraiser Selected",
        dataType: DATA_TYPE_MULTI_LINE_STRING,
        value: assignmentApproval.requestedAppraiserDescription,
        editable: false
      },
      {
        key: "availableAppraisers",
        label: "Available Appraisers",
        dataType: DATA_TYPE_STRING,
        value: assignmentApproval.availableAppraisersDescription,
        editable: false
      },
      {
        key: "proximityAssignmentRequest",
        label: "Proximity Assignment Request",
        dataType: DATA_TYPE_MULTI_LINE_STRING,
        value: (!assignmentApproval.requestApprovalComments) ?
          assignmentApproval.requestApprovalReasons :
          `${assignmentApproval.requestApprovalReasons}\n ${assignmentApproval.requestApprovalComments}`,
        editable: false
      },
      {
        key: "proximityAssignmentDecision",
        label: "Proximity Assignment Decision",
        dataType: DATA_TYPE_STRING,
        value: assignmentApproval.decisionComments,
        editable: isEditable,
        validation: yup.string().nullable().required("Proximity Assignment Decision is a required field")
      },
    );
    return [...fields];
  };

  const handleSave = (arr, obj) => {
    if(cardControlMethods.runValidation(true)){
      setSendingAssignmentApproval(true);
      let payload = {
        "controlIdNumber": controlNumber,
        "appraisalAssignmentApprovalId": assignmentApproval.id,
        "decisionDateUtc": moment.utc(new Date()),
        "decisionMade": isDecided(obj.approvalDecision),
        "decisionInternetUserId": internetUser,
        "decisionApproved": obj.approvalDecision ? isApproved(obj.approvalDecision) : assignmentApproval.decisionApproved,
        "decisionComments": obj.proximityAssignmentDecision ? obj.proximityAssignmentDecision : assignmentApproval.decisionComments
      };
      setAppraiserAssignmentApproval(controlNumber, payload,
        (resp) => {
          setAssignmentApproval(resp);
          setSendingAssignmentApproval(false);
          makeSuccessToast(`Appraiser Assignment: ${obj.approvalDecision}`);
        },
        () => {setSendingAssignmentApproval(false);},  mounted);
      return true;
    } else return false;
  };

  const setCardControls = (cm) => {setCardControlMethods(cm);};

  if (fetchingAssignmentApproval || sendingAssignmentApproval) {
    return <MSpinner />;
  }

  if(!assignmentApproval) {
    return (
      <MCard
        title="Appraiser Assignment Approval"
        fields={[]}
      />
    );
  }

  return (
    <MCard
      title="Appraiser Assignment Approval"
      fields={getFields()}
      isEditable={isEditable && isVisible}
      getControlMethods={setCardControls}
      onEditFinished={handleSave}
    />
  );
};

AppraiserAssignmentApproval.propTypes = {
  isEditable: PropTypes.bool,
  controlNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isVisible: PropTypes.bool,
  internetUser: PropTypes.number,
  makeSuccessToast: PropTypes.func,
};

const mapStateToProps = state => {
  return {
    internetUser: state.user.profile.internetUserId,
    isEditable: userHasRight(APPRAISAL_ASSIGNMENT_APPROVAL_EDIT)(state),
    isVisible: userHasRight(APPRAISAL_ASSIGNMENT_APPROVAL_VIEW)(state),
  };
};

const mapDispatchToProps = dispatch => ({
  makeSuccessToast(text) {
    responseHandler.customToast(text, "success", dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AppraiserAssignmentApproval);
